// Shareable Wrapped card — 1080×1920 (IG/TikTok/Snap story format).
// Same export approach as PersonaShareCard: fixed inline pixel styles so the
// PNG exported by html-to-image matches the design exactly.
import { forwardRef } from "react";

const FONT_DISPLAY = "'Bebas Neue', Impact, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const RED = "#FF3B30";
const BG = "#050505";
const LOGO_SRC = "/brand/otaku-index-logo-dark.webp";

const monoLabel = (size) => ({
  fontFamily: FONT_MONO,
  fontSize: `${size}px`,
  letterSpacing: "0.35em",
  textTransform: "uppercase",
  color: "#888",
  margin: 0,
});

const WrappedShareCard = forwardRef(function WrappedShareCard({ data }, ref) {
  const W = 1080;
  const H = 1920;
  const PAD = 80;
  const stats = [
    { label: "Completed", value: data.total_completed ?? 0 },
    { label: "Hours", value: data.total_hours ?? 0 },
    { label: "Top Genre", value: data.top_genre || "—", accent: true },
    { label: "Avg Rating", value: data.average_rating ?? "—" },
  ];

  return (
    <div
      ref={ref}
      data-testid="wrapped-share-card"
      style={{
        width: `${W}px`,
        height: `${H}px`,
        backgroundColor: BG,
        color: "#fff",
        fontFamily: FONT_BODY,
        padding: `${PAD}px`,
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Red radial glow */}
      <div
        style={{
          position: "absolute",
          top: "-20%",
          right: "-35%",
          width: "1300px",
          height: "1300px",
          background: `radial-gradient(circle at center, ${RED}22 0%, transparent 60%)`,
          pointerEvents: "none",
        }}
      />
      {/* Corner tick */}
      <div
        style={{
          position: "absolute",
          top: `${PAD - 12}px`,
          right: `${PAD - 12}px`,
          width: "34px",
          height: "34px",
          borderTop: `3px solid ${RED}`,
          borderRight: `3px solid ${RED}`,
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          paddingBottom: "24px",
        }}
      >
        <img
          src={LOGO_SRC}
          alt="Otaku Index"
          crossOrigin="anonymous"
          style={{ height: "60px", width: "auto", objectFit: "contain" }}
        />
        <span style={monoLabel(18)}>Year in Review</span>
      </div>

      {/* Big year heading */}
      <div style={{ position: "relative", marginTop: "70px" }}>
        <p style={monoLabel(18)}>My</p>
        <h1
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: "260px",
            lineHeight: 0.85,
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
            color: "#fff",
            margin: "16px 0 0 0",
          }}
        >
          {data.year}
        </h1>
        <p
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: "80px",
            lineHeight: 1.0,
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
            color: RED,
            margin: "12px 0 0 0",
          }}
        >
          Wrapped
        </p>
      </div>

      {/* Featured highlight (highest rated preferred, else longest) */}
      <Highlight data={data} />

      {/* Stats — 2×2 grid */}
      <div
        style={{
          position: "relative",
          marginTop: "48px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "28px",
        }}
      >
        {stats.map((s) => (
          <div
            key={s.label}
            style={{
              borderLeft: `3px solid ${s.accent ? RED : "rgba(255,255,255,0.25)"}`,
              paddingLeft: "24px",
              minWidth: 0,
            }}
          >
            <p style={{ ...monoLabel(18), color: "#888" }}>{s.label}</p>
            <p
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: "72px",
                lineHeight: 1,
                letterSpacing: "-0.01em",
                textTransform: "uppercase",
                color: "#fff",
                margin: "14px 0 0 0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Persona */}
      {data.persona?.personality_type && (
        <div style={{ position: "relative", marginTop: "48px" }}>
          <p style={monoLabel(18)}>Your Persona</p>
          <p
            style={{
              fontFamily: FONT_DISPLAY,
              fontSize: "58px",
              lineHeight: 1.0,
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              color: "#fff",
              margin: "12px 0 0 0",
            }}
          >
            {data.persona.personality_type}
          </p>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          position: "relative",
          marginTop: "auto",
          paddingTop: "24px",
          borderTop: "1px solid rgba(255,255,255,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: FONT_MONO,
          fontSize: "22px",
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: "#888",
        }}
      >
        <span style={{ color: "#fff" }}>otakuindex.app</span>
        <span>Track · Recap · Share</span>
      </div>
    </div>
  );
});

function Highlight({ data }) {
  const entry = data.highest_rated || data.longest_series;
  if (!entry) return <div style={{ marginTop: "60px" }} />;
  const isHighest = !!data.highest_rated;
  return (
    <div
      style={{
        position: "relative",
        marginTop: "60px",
        display: "flex",
        gap: "32px",
        alignItems: "flex-start",
      }}
    >
      {entry.image_url && (
        <img
          src={entry.image_url}
          alt={entry.title}
          crossOrigin="anonymous"
          style={{
            width: "260px",
            height: "370px",
            objectFit: "cover",
            border: "2px solid rgba(255,255,255,0.15)",
            flexShrink: 0,
          }}
        />
      )}
      <div style={{ paddingTop: "8px" }}>
        <p style={monoLabel(16)}>{isHighest ? "Top Rated" : "Longest Watched"}</p>
        <h2
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: "56px",
            lineHeight: 1.0,
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
            color: "#fff",
            margin: "16px 0 0 0",
            wordBreak: "break-word",
          }}
        >
          {entry.title}
        </h2>
        <p
          style={{
            fontFamily: FONT_MONO,
            fontSize: "22px",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "#c8c8c8",
            margin: "18px 0 0 0",
          }}
        >
          {isHighest
            ? `You gave it ${entry.user_rating}/10`
            : `${entry.episodes} episodes`}
        </p>
      </div>
    </div>
  );
}

export default WrappedShareCard;
